"use client";
import {NeoButton} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {memo, useEffect, useState} from "react";
import toast from "react-hot-toast";
import {AiOutlineLoading, AiTwotoneFolderOpen} from "react-icons/ai";

type Props = {
  title?: string;
  data: any;
  operation?: boolean;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
};

const formatBytes = (bytes?: number) => {
  if (!Number.isFinite(bytes as number)) return "-";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes!) / Math.log(1024)),
    u.length - 1
  );
  const v = bytes! / Math.pow(1024, i);
  return `${v % 1 === 0 ? v : v.toFixed(1)} ${u[i]}`;
};

const displaySize = (f: any) =>
  typeof f.sizeBytes === "number"
    ? formatBytes(f.sizeBytes)
    : (f.fileSize ?? "-");

function getExt(name?: string) {
  return (name?.split(".").pop() || "").toLowerCase();
}

const TypeBadge = memo(({file}: {file: any}) => {
  const ext = getExt(file?.name);
  const isImageExt = [
    "png",
    "jpg",
    "jpeg",
    "webp",
    "gif",
    "bmp",
    "svg"
  ].includes(ext);
  if (ext === "pdf") {
    return (
      <svg
        width="29"
        height="31"
        viewBox="0 0 29 31"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          d="M0.349609 4.42556C0.349609 2.40203 1.9948 0.756836 4.01833 0.756836H13.1901V8.09427C13.1901 9.1089 14.0099 9.92863 15.0245 9.92863H22.3619V18.1833H10.4386C8.41506 18.1833 6.76987 19.8284 6.76987 21.852V30.1066H4.01833C1.9948 30.1066 0.349609 28.4614 0.349609 26.4379V4.42556ZM22.3619 8.09427H15.0245V0.756836L22.3619 8.09427ZM10.4386 20.9348H12.2729C14.0442 20.9348 15.4831 22.3736 15.4831 24.1449C15.4831 25.9162 14.0442 27.3551 12.2729 27.3551H11.3558V29.1894C11.3558 29.6939 10.943 30.1066 10.4386 30.1066C9.93414 30.1066 9.52141 29.6939 9.52141 29.1894V26.4379V21.852C9.52141 21.3475 9.93414 20.9348 10.4386 20.9348ZM12.2729 25.5207C13.0354 25.5207 13.6487 24.9073 13.6487 24.1449C13.6487 23.3825 13.0354 22.7692 12.2729 22.7692H11.3558V25.5207H12.2729ZM17.776 20.9348H19.6104C21.1295 20.9348 22.3619 22.1673 22.3619 23.6863V27.3551C22.3619 28.8741 21.1295 30.1066 19.6104 30.1066H17.776C17.2716 30.1066 16.8588 29.6939 16.8588 29.1894V21.852C16.8588 21.3475 17.2716 20.9348 17.776 20.9348ZM19.6104 28.2722C20.1148 28.2722 20.5276 27.8595 20.5276 27.3551V23.6863C20.5276 23.1819 20.1148 22.7692 19.6104 22.7692H18.6932V28.2722H19.6104ZM24.1963 21.852C24.1963 21.3475 24.609 20.9348 25.1135 20.9348H27.865C28.3695 20.9348 28.7822 21.3475 28.7822 21.852C28.7822 22.3564 28.3695 22.7692 27.865 22.7692H26.0306V24.6035H27.865C28.3695 24.6035 28.7822 25.0162 28.7822 25.5207C28.7822 26.0251 28.3695 26.4379 27.865 26.4379H26.0306V29.1894C26.0306 29.6939 25.6179 30.1066 25.1135 30.1066C24.609 30.1066 24.1963 29.6939 24.1963 29.1894V25.5207V21.852Z"
          fill="white"
        />
      </svg>
    );
  }

  if (isImageExt) {
    return (
      <svg
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_11261_47460)">
          <path
            d="M0 3.66872C0 1.64519 1.64519 0 3.66872 0H12.8405V7.33744C12.8405 8.35207 13.6602 9.1718 14.6749 9.1718H22.0123V17.4264H10.089C8.06545 17.4264 6.42026 19.0716 6.42026 21.0951V29.3498H3.66872C1.64519 29.3498 0 27.7046 0 25.681V3.66872ZM22.0123 7.33744H14.6749V0L22.0123 7.33744Z"
            fill="white"
          />
          <path
            d="M8.00476 27.8419L8.01429 27.0816L8.02382 25.6668C8.03018 25.4615 8.03335 25.2498 8.03335 25.0316C8.03335 24.8135 8.03335 24.5889 8.03335 24.3579C8.03335 24.1269 8.03335 23.9216 8.03335 23.742C8.03335 23.5623 8.0397 23.3602 8.05241 23.1357V23.0972C8.05876 23.033 8.06194 22.9688 8.06194 22.9047C8.06829 22.8341 8.07147 22.7635 8.07147 22.6929C8.07782 22.5775 8.11276 22.4523 8.17629 22.3176C8.22075 22.2278 8.28746 22.1508 8.37639 22.0866C8.47804 22.0353 8.5765 22.0096 8.67179 22.0096C8.72261 22.0096 8.76708 22.0128 8.8052 22.0192C8.84967 22.0192 8.89096 22.0225 8.92908 22.0289C9.01166 22.0417 9.08472 22.0802 9.14824 22.1444C9.18636 22.1764 9.2213 22.2278 9.25306 22.2984C9.26577 22.3368 9.27529 22.3753 9.28165 22.4138C9.29435 22.4459 9.30388 22.4812 9.31023 22.5197C9.31659 22.5646 9.31976 22.6063 9.31976 22.6448C9.32612 22.6833 9.32929 22.7218 9.32929 22.7603C9.32929 22.8052 9.32612 22.8469 9.31976 22.8854C9.31976 22.9175 9.31976 22.9528 9.31976 22.9913V23.1549L9.26259 28.1499V28.2461C9.26259 28.2654 9.25941 28.291 9.25306 28.3231C9.25306 28.3488 9.24988 28.3808 9.24353 28.4193C9.23083 28.4514 9.21812 28.4867 9.20542 28.5252C9.19906 28.5573 9.18953 28.5926 9.17683 28.6311C9.15777 28.6632 9.13554 28.6952 9.11013 28.7273C9.09107 28.753 9.06566 28.7786 9.03389 28.8043C8.98307 28.8556 8.90049 28.8909 8.78614 28.9102C8.61462 28.9487 8.45263 28.9326 8.30016 28.8621C8.22393 28.8107 8.16993 28.7466 8.13817 28.6696C8.11911 28.6503 8.10958 28.6311 8.10958 28.6118C8.10323 28.599 8.09688 28.5894 8.09053 28.583C8.08417 28.5765 8.07782 28.5669 8.07147 28.5541C8.05876 28.522 8.04923 28.4899 8.04288 28.4578C8.03653 28.4193 8.03335 28.3808 8.03335 28.3423C8.027 28.3103 8.02065 28.2782 8.01429 28.2461C8.01429 28.214 8.01112 28.1819 8.00476 28.1499C7.99841 28.0985 7.99841 28.044 8.00476 27.9863V27.8419Z"
            fill="white"
          />
          <path
            d="M16.1832 28.1691C16.1769 28.2461 16.1673 28.3263 16.1546 28.4097C16.1419 28.4931 16.1197 28.5701 16.0879 28.6407C16.0562 28.7177 16.0149 28.7754 15.9641 28.8139C15.8942 28.8781 15.8116 28.9134 15.7163 28.9198C15.6274 28.9519 15.5416 28.9615 15.459 28.9487C15.3764 28.9358 15.3002 28.9134 15.2303 28.8813C15.1414 28.8236 15.0747 28.7562 15.0302 28.6792C14.9921 28.5958 14.9635 28.5092 14.9445 28.4193C14.9254 28.3295 14.9159 28.2365 14.9159 28.1402C14.9159 28.044 14.9159 27.9542 14.9159 27.8708V27.1201L14.9063 25.6476C14.9063 25.5449 14.9063 25.4647 14.9063 25.407C14.9063 25.3492 14.9063 25.2979 14.9063 25.253C14.9063 25.2016 14.9032 25.1471 14.8968 25.0894C14.8968 25.0252 14.8968 24.9354 14.8968 24.8199C14.8968 24.8006 14.8968 24.7686 14.8968 24.7236C14.9032 24.6723 14.9 24.5408 14.8873 24.3291C14.6395 24.7461 14.4331 25.083 14.2679 25.3396C14.1091 25.5898 13.9725 25.7855 13.8582 25.9267C13.7502 26.0614 13.6549 26.1544 13.5723 26.2058C13.4897 26.2507 13.4039 26.2764 13.315 26.2828C13.2642 26.2892 13.2134 26.2796 13.1625 26.2539C13.1117 26.2282 13.0609 26.1962 13.0101 26.1577C12.9656 26.1192 12.9211 26.0807 12.8767 26.0422C12.8386 25.9973 12.8068 25.9555 12.7814 25.917C12.6289 25.7246 12.4574 25.4936 12.2668 25.2241C12.0762 24.9546 11.8793 24.6274 11.676 24.2424C11.676 24.2489 11.6728 24.3547 11.6665 24.56C11.6665 24.7654 11.6665 25.022 11.6665 25.33C11.6665 25.6315 11.6633 25.9588 11.657 26.3116C11.657 26.6581 11.6538 26.9821 11.6474 27.2837C11.6474 27.5852 11.6443 27.8387 11.6379 28.044C11.6316 28.2429 11.6252 28.3423 11.6189 28.3423C11.6189 28.4514 11.5934 28.5541 11.5426 28.6503C11.4918 28.7401 11.4219 28.8139 11.333 28.8717C11.2504 28.9294 11.1519 28.9615 11.0376 28.9679C10.9232 28.9743 10.7994 28.9423 10.666 28.8717C10.5834 28.8203 10.5198 28.7466 10.4754 28.6503C10.4373 28.5541 10.4118 28.4643 10.3991 28.3808C10.3928 28.291 10.3864 28.2044 10.3801 28.121C10.3801 28.0376 10.3801 27.951 10.3801 27.8611L10.3896 27.1104L10.3991 25.6283V24.3387C10.3991 23.7805 10.4182 23.2319 10.4563 22.6929C10.4563 22.6224 10.4627 22.5582 10.4754 22.5005C10.4944 22.4363 10.5167 22.3753 10.5421 22.3176C10.5929 22.2149 10.6596 22.1379 10.7422 22.0866C10.8375 22.0289 10.9391 22 11.0471 22C11.0916 22 11.1329 22.0032 11.171 22.0096C11.2155 22.0096 11.2568 22.0128 11.2949 22.0192C11.4219 22.0449 11.5299 22.1219 11.6189 22.2502C11.7141 22.3721 11.8031 22.478 11.8857 22.5678C11.9365 22.6256 11.9905 22.6962 12.0477 22.7796C12.1048 22.863 12.162 22.9496 12.2192 23.0394C12.2764 23.1292 12.3303 23.2159 12.3812 23.2993C12.432 23.3827 12.4765 23.4565 12.5146 23.5206C12.6797 23.7901 12.8259 24.0371 12.9529 24.2617C13.0863 24.4863 13.2102 24.6787 13.3245 24.8391C13.3436 24.8199 13.3658 24.7942 13.3912 24.7621C13.4167 24.7236 13.4421 24.6852 13.4675 24.6467C13.4992 24.6017 13.5246 24.56 13.5437 24.5215C13.5691 24.483 13.5882 24.451 13.6009 24.4253C13.6644 24.3226 13.7279 24.2232 13.7915 24.1269C13.855 24.0243 13.9217 23.9184 13.9916 23.8093C14.0678 23.6939 14.144 23.5687 14.2203 23.434C14.3028 23.2993 14.3981 23.1453 14.5061 22.972C14.5506 22.8951 14.6078 22.802 14.6777 22.6929C14.7475 22.5839 14.8269 22.478 14.9159 22.3753C15.0048 22.2727 15.0969 22.1861 15.1922 22.1155C15.2939 22.0385 15.3955 22 15.4971 22C15.548 22 15.5924 22.0032 15.6306 22.0096C15.6687 22.0096 15.7068 22.0128 15.7449 22.0192C15.8211 22.0257 15.8942 22.061 15.9641 22.1251C16.0149 22.1764 16.053 22.231 16.0784 22.2887C16.1038 22.3721 16.1197 22.4427 16.1261 22.5005C16.1324 22.5454 16.1356 22.5871 16.1356 22.6256C16.1419 22.6641 16.1451 22.7026 16.1451 22.7411C16.1451 23.1004 16.1483 23.4565 16.1546 23.8093C16.161 24.1622 16.1642 24.5183 16.1642 24.8776C16.1642 25.3139 16.1642 25.7631 16.1642 26.225C16.1705 26.687 16.1737 27.1361 16.1737 27.5724C16.1737 27.6622 16.1769 27.7585 16.1832 27.8611C16.1896 27.9638 16.1896 28.0665 16.1832 28.1691Z"
            fill="white"
          />
          <path
            d="M22 26.5715C22 26.764 21.9905 26.9597 21.9714 27.1586C21.9524 27.3511 21.9142 27.5403 21.8571 27.7264C21.7999 27.906 21.7141 28.0793 21.5998 28.2461C21.4854 28.4065 21.3298 28.5477 21.1329 28.6696C21.0376 28.7337 20.9232 28.7883 20.7898 28.8332C20.6628 28.8717 20.5262 28.9038 20.3801 28.9294C20.2403 28.9615 20.1006 28.9808 19.9608 28.9872C19.821 29 19.6908 29.0032 19.5701 28.9968C18.9984 28.9775 18.5346 28.8396 18.1789 28.583C17.8295 28.3199 17.5595 27.9959 17.3689 27.6109C17.1847 27.2259 17.064 26.8089 17.0068 26.3598C16.956 25.9042 16.9465 25.4711 16.9782 25.0605C16.9973 24.8167 17.0386 24.5536 17.1021 24.2713C17.172 23.989 17.2705 23.7163 17.3975 23.4533C17.5309 23.1902 17.6929 22.9528 17.8835 22.7411C18.0804 22.5293 18.3123 22.3689 18.5791 22.2599C18.8269 22.1572 19.0968 22.093 19.3891 22.0674C19.6876 22.0353 19.9799 22.0449 20.2657 22.0962C20.5579 22.1476 20.8279 22.2406 21.0757 22.3753C21.3298 22.5101 21.5299 22.6897 21.676 22.9143C21.7777 23.0811 21.8571 23.2832 21.9142 23.5206C21.9714 23.758 21.9809 23.9858 21.9428 24.2039C21.9111 24.3964 21.8444 24.5312 21.7427 24.6082C21.6411 24.6787 21.5299 24.7076 21.4092 24.6948C21.2885 24.6755 21.171 24.6242 21.0566 24.5408C20.9486 24.451 20.8756 24.3387 20.8375 24.2039C20.793 24.0564 20.739 23.9216 20.6755 23.7997C20.6119 23.6714 20.5135 23.5655 20.3801 23.4821C20.2276 23.3859 20.0656 23.3313 19.8941 23.3185C19.7289 23.3057 19.5669 23.3249 19.4081 23.3763C19.2557 23.4276 19.1095 23.5078 18.9698 23.6169C18.8364 23.7259 18.7252 23.8511 18.6363 23.9922C18.5283 24.1526 18.4457 24.3291 18.3885 24.5215C18.3313 24.7076 18.2901 24.9033 18.2646 25.1086C18.2392 25.3075 18.2297 25.5096 18.2361 25.7149C18.2488 25.9138 18.2742 26.1063 18.3123 26.2924C18.3822 26.6068 18.4902 26.9019 18.6363 27.1778C18.6934 27.2805 18.7697 27.3735 18.865 27.4569C18.9666 27.5339 19.0778 27.5981 19.1985 27.6494C19.3192 27.6943 19.4462 27.7232 19.5796 27.736C19.713 27.7489 19.8401 27.7392 19.9608 27.7071C20.1323 27.6622 20.2721 27.6045 20.3801 27.5339C20.4881 27.4569 20.5738 27.3671 20.6374 27.2644C20.7009 27.1618 20.7422 27.0495 20.7612 26.9276C20.7866 26.8057 20.7993 26.6774 20.7993 26.5426V26.533C20.7168 26.533 20.6437 26.533 20.5802 26.533C20.5167 26.5266 20.469 26.5234 20.4372 26.5234C20.2975 26.5234 20.1609 26.4977 20.0275 26.4464C19.9132 26.4079 19.8274 26.3469 19.7702 26.2635C19.7067 26.1929 19.6749 26.1063 19.6749 26.0037C19.6749 25.9652 19.6749 25.9299 19.6749 25.8978C19.6813 25.8593 19.6876 25.8208 19.694 25.7823C19.713 25.731 19.7543 25.6668 19.8179 25.5898C19.856 25.5577 19.9163 25.5321 19.9989 25.5128C20.037 25.5 20.0751 25.4904 20.1133 25.484C20.1514 25.4711 20.1958 25.4615 20.2467 25.4551C20.2848 25.4487 20.3261 25.4455 20.3705 25.4455C20.415 25.4455 20.4595 25.4455 20.504 25.4455H21.3711C21.441 25.439 21.5204 25.4487 21.6093 25.4743C21.7046 25.4936 21.7777 25.5257 21.8285 25.5706C21.8475 25.5962 21.8666 25.6219 21.8857 25.6476C21.9047 25.6732 21.9206 25.7021 21.9333 25.7342C21.9587 25.8048 21.9746 25.8753 21.9809 25.9459C21.9873 25.9844 21.9905 26.0261 21.9905 26.071C21.9968 26.1095 22 26.1448 22 26.1769V26.5715Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_11261_47460">
            <path d="M0 0H29.3498V29.3498H0V0Z" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  // default / other
  return (
    <svg
      width="29"
      height="31"
      viewBox="0 0 29 31"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M0.349609 4.42556C0.349609 2.40203 1.9948 0.756836 4.01833 0.756836H13.1901V8.09427C13.1901 9.1089 14.0099 9.92863 15.0245 9.92863H22.3619V18.1833H10.4386C8.41506 18.1833 6.76987 19.8284 6.76987 21.852V30.1066H4.01833C1.9948 30.1066 0.349609 28.4614 0.349609 26.4379V4.42556ZM22.3619 8.09427H15.0245V0.756836L22.3619 8.09427ZM10.4386 20.9348H12.2729C14.0442 20.9348 15.4831 22.3736 15.4831 24.1449C15.4831 25.9162 14.0442 27.3551 12.2729 27.3551H11.3558V29.1894C11.3558 29.6939 10.943 30.1066 10.4386 30.1066C9.93414 30.1066 9.52141 29.6939 9.52141 29.1894V26.4379V21.852C9.52141 21.3475 9.93414 20.9348 10.4386 20.9348ZM12.2729 25.5207C13.0354 25.5207 13.6487 24.9073 13.6487 24.1449C13.6487 23.3825 13.0354 22.7692 12.2729 22.7692H11.3558V25.5207H12.2729ZM17.776 20.9348H19.6104C21.1295 20.9348 22.3619 22.1673 22.3619 23.6863V27.3551C22.3619 28.8741 21.1295 30.1066 19.6104 30.1066H17.776C17.2716 30.1066 16.8588 29.6939 16.8588 29.1894V21.852C16.8588 21.3475 17.2716 20.9348 17.776 20.9348ZM19.6104 28.2722C20.1148 28.2722 20.5276 27.8595 20.5276 27.3551V23.6863C20.5276 23.1819 20.1148 22.7692 19.6104 22.7692H18.6932V28.2722H19.6104ZM24.1963 21.852C24.1963 21.3475 24.609 20.9348 25.1135 20.9348H27.865C28.3695 20.9348 28.7822 21.3475 28.7822 21.852C28.7822 22.3564 28.3695 22.7692 27.865 22.7692H26.0306V24.6035H27.865C28.3695 24.6035 28.7822 25.0162 28.7822 25.5207C28.7822 26.0251 28.3695 26.4379 27.865 26.4379H26.0306V29.1894C26.0306 29.6939 25.6179 30.1066 25.1135 30.1066C24.609 30.1066 24.1963 29.6939 24.1963 29.1894V25.5207V21.852Z"
        fill="white"
      />
    </svg>
  );
});
TypeBadge.displayName = "TypeBadge";

export const DocumentsGrid: React.FC<Props> = ({
  title = "Documents",
  data = {},
  operation = false,
  onSubmit
}) => {
  const {filterContext, offset} = useNeoContext();
  const [loginUserId, setLoginUserId] = useState<string>("");
  const [attachments, setAttachments] = useState<any>([]);
  const [newattachments, setNewAttachments] = useState<any>([]);
  const [reportType, setReportType] = useState<any>("");
  useEffect(() => {
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext]);

  useEffect(() => {
    /* 1-FUEL,2-MAINTENANCE,3-MISCELLANEOUS*/
    if (loginUserId && data?.reportType) {
      setReportType(data.reportType);
    }
    if (reportType) {
      getdocumentdata();
    } else {
      setAttachments(Array.isArray(data?.documents) ? data.documents : []);
    }
  }, [loginUserId, data, reportType]);

  const getdocumentdata = async () => {
    const requestdata = {
      expenseType: reportType,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        let responsedata = response?.data?.data;
        let documentdata: any = [];
        if (reportType === 1) {
          documentdata = responsedata.fuelExpense;
        } else if (reportType === 2) {
          documentdata = responsedata.maintenanceExpense;
        } else if (reportType === 3) {
          documentdata = responsedata.miscExpense;
        }
        setAttachments(
          Array.isArray(documentdata?.documents) ? documentdata.documents : []
        );
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  };

  const onDownload = async (data: any) => {
    try {
      // fetch the file as blob
      const res = await fetch(data.filePath, {mode: "cors"});
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const blob = await res.blob();

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = data.name;
      document.body.appendChild(a);
      a.click();

      // cleanup
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      //console.log(err);
    }
  };

  const onDelete = async (data: any) => {
    if (data?.id) {
      const requestdata = {
        id: data?.id,
        operation: "documentdelete"
      };
      onSubmit(requestdata);
    }
  };

  const onCancel = () => {
    setNewAttachments([]);
  };

  const onDocumentSubmit = () => {
    if (newattachments.length > 0) {
      let requestdata: any = {
        expenseType: data?.reportType,
        expenseRefId: data?.id,
        files: newattachments
      };
      onSubmit(requestdata);
    }
  };

  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-yellow-400">
          <AiTwotoneFolderOpen className="h-5 w-5 text-white [&_*]:fill-current [&_*]:stroke-current [&_*]:opacity-100" />
        </span>
        <div className="leading-tight">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-600">
            {attachments.length}{" "}
            {attachments.length === 1 ? "document" : "documents"} attached
          </p>
        </div>
      </div>
      <div
        className={`
          grid gap-4
          ${attachments.length === 1 ? "grid-cols-1" : ""}
          ${attachments.length === 2 ? "grid-cols-2" : ""}
          ${attachments.length === 3 ? "grid-cols-3" : ""}
        `}
      >
        {attachments.map((file: any) => (
          <div
            key={file.id}
            className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm"
          >
            <div className="relative h-28 bg-black text-white">
              <div className="flex items-start justify-between p-3">
                <div className="grid items-center rounded-lg bg-white/15 p-2">
                  <TypeBadge file={file} />
                </div>
                {reportType && onDelete && (
                  <button
                    onClick={() => onDelete(file)}
                    className="grid place-items-center rounded-lg bg-white/15 p-2"
                    aria-label="Delete file"
                    title="Delete"
                    type="button"
                  >
                    <svg
                      width="19"
                      height="21"
                      viewBox="0 0 19 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clip-path="url(#clip0_7184_15922)">
                        <path
                          d="M6.10233 1.5358C6.30869 1.11924 6.73289 0.859375 7.1953 0.859375H11.7965C12.2589 0.859375 12.6831 1.11924 12.8895 1.5358L13.1646 2.08228H16.8333C17.5098 2.08228 18.0562 2.62877 18.0562 3.30519C18.0562 3.98161 17.5098 4.52809 16.8333 4.52809H2.15845C1.48203 4.52809 0.935547 3.98161 0.935547 3.30519C0.935547 2.62877 1.48203 2.08228 2.15845 2.08228H5.82717L6.10233 1.5358ZM2.15845 5.751H16.8333V17.9801C16.8333 19.3291 15.7365 20.4259 14.3875 20.4259H4.60427C3.25525 20.4259 2.15845 19.3291 2.15845 17.9801V5.751ZM5.82717 8.19681C5.49087 8.19681 5.21572 8.47197 5.21572 8.80827V17.3686C5.21572 17.7049 5.49087 17.9801 5.82717 17.9801C6.16347 17.9801 6.43863 17.7049 6.43863 17.3686V8.80827C6.43863 8.47197 6.16347 8.19681 5.82717 8.19681ZM9.49589 8.19681C9.15959 8.19681 8.88444 8.47197 8.88444 8.80827V17.3686C8.88444 17.7049 9.15959 17.9801 9.49589 17.9801C9.83219 17.9801 10.1073 17.7049 10.1073 17.3686V8.80827C10.1073 8.47197 9.83219 8.19681 9.49589 8.19681ZM13.1646 8.19681C12.8283 8.19681 12.5532 8.47197 12.5532 8.80827V17.3686C12.5532 17.7049 12.8283 17.9801 13.1646 17.9801C13.5009 17.9801 13.7761 17.7049 13.7761 17.3686V8.80827C13.7761 8.47197 13.5009 8.19681 13.1646 8.19681Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_7184_15922">
                          <path
                            d="M0.935547 0.859375H18.0562V20.4259H0.935547V0.859375Z"
                            fill="white"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                )}
              </div>
              <div className="absolute inset-x-0 bottom-0 px-4 pb-3">
                <p className="text-md truncate font-medium">{file.name}</p>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">File Size</div>
                <div className="text-sm font-semibold text-gray-900">
                  {displaySize(file)}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end">
                <div className="flex items-center gap-2">
                  <a href={file.filePath} target="_blank" title="View">
                    <button className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                      View
                    </button>
                  </a>
                  <button
                    className="h-9 rounded-md bg-gray-900 px-3 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => onDownload(file)}
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {reportType && data?.documents?.length < 3 && (
        <>
          <div className="mb-1 grid grid-cols-1">
            <UploadFormField
              value={newattachments}
              onChangeFile={setNewAttachments}
              className={"grid-cols-1"}
              max={3 - attachments.length}
              onError={msg => console.error(msg)}
            />
          </div>
          <hr />
          <div className="flex items-center justify-end gap-2 p-1">
            <NeoButton type="button" color="white" onClick={onCancel}>
              Cancel
            </NeoButton>
            <NeoButton
              type="submit"
              spinner={
                <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
              }
              color="black"
              onClick={onDocumentSubmit}
            >
              Upload
            </NeoButton>
          </div>
        </>
      )}
    </>
  );
};
